import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Button, CircularProgress, Alert, Chip,
  InputAdornment, Avatar, Stack, Typography, Grid, Stepper, Step, StepLabel,
  RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, Autocomplete,
  Paper, Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Badge as BadgeIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Close as CloseIcon, PersonOutline as PersonIcon, WorkOutline as WorkIcon,
  AccountBalanceWallet as SalaryIcon, AccountBalance as BankIcon,
  ContactPhone as KinIcon, EventBusy as LeaveIcon, Tune as FilterIcon,
  Groups as DeptIcon, People as PeopleIcon, AutoAwesome as AutoIcon,
  ArrowBack as PrevIcon, ArrowForward as NextIcon, CheckCircle as DoneIcon,
  Redeem as AllowanceIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ──────────────────────────────────────────────────────────────────
// Shared table styling — uppercase 10.5px headers, zebra rows, hover
// tint, tabular numerics. Matches HR Employees + Examination pages.
const PRO_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 800, fontSize: 10.5, letterSpacing: 0.6,
    textTransform: 'uppercase', color: 'text.secondary',
    bgcolor: '#f7f9fc', py: 1, borderBottom: '1px solid #d9dee6',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'background-color 120ms ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even):not(.row-leave)':
    { backgroundColor: '#fafbfc' },
  '& .MuiTableBody-root .MuiTableRow-root:hover':
    { backgroundColor: 'rgba(25,118,210,0.06) !important' },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5, py: 1.05, borderBottom: '1px solid #eef0f4',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .MuiTableBody-root .MuiTableRow-root.row-leave .MuiTableCell-root': {
    color: 'text.disabled',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 0,
  },
};

const blank = {
  employee_code: '', first_name: '', last_name: '', gender: 'Male', date_of_birth: '',
  designation: '', ssnit_number: '', phone: '', bank_name: '', account_number: '',
  basic_salary: 0, payroll_mode: 'Bank', department: '', email: '', address: '',
  join_date: '', next_of_kin: '', next_of_kin_phone: '', tin_number: '', pin_number: '',
  leav: 'no', employment_type: 'Permanent staff',
};

const EMP_TYPES = ['Permanent staff', 'Contract', 'Casual'];
const EMP_TYPE_TONE = {
  'Permanent staff': 'success',
  'Contract':        'warning',
  'Casual':          'info',
};

// Department + Position dropdowns now come from the master tables
// (HR Settings → Departments / Positions). Seeded on first install
// with the curated school list; admins edit and add freely from
// then on.

const initials = (a, b) =>
  `${(a || '').trim()[0] || ''}${(b || '').trim()[0] || ''}`.toUpperCase();
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ──────────────────────────────────────────────────────────────────
// Dialog section + field helpers
// ──────────────────────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
          color: 'primary.main',
        }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 12.5,
          textTransform: 'uppercase', letterSpacing: 0.7, color: 'text.secondary' }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider', mb: -0.5 }} />
      </Stack>
      <Grid container spacing={2}>{children}</Grid>
    </Box>
  );
}
function Field({ name, label, value, onChange, type = 'text', select, options, xs = 12, sm = 6, ...rest }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <TextField fullWidth size="small" type={type} label={label}
        value={value ?? ''} onChange={(e) => onChange(name, e.target.value)}
        select={!!select}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        {...rest}>
        {select && options?.map((o) =>
          <MenuItem key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
            {typeof o === 'string' ? o : o.label}
          </MenuItem>)}
      </TextField>
    </Grid>
  );
}

// ──────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────
export default function EmployeesPage() {
  const [rows, setRows]  = useState([]);
  const [loading, setL]  = useState(true);
  const [q, setQ]        = useState('');
  const [dep, setDep]    = useState('');
  const [emp, setEmp]    = useState('');

  const [open, setOpen]  = useState(false);
  const [editing, setEd] = useState(null);
  const [form, setForm]  = useState(blank);
  const [saving, setSav] = useState(false);
  const [error, setErr]  = useState('');

  // Department + Position options come from the master tables now
  // (HR Settings → Departments / Positions). User can still type a
  // custom value because the Autocompletes stay in freeSolo mode.
  const [deptOpts, setDeptOpts] = useState([]);
  const [posOpts,  setPosOpts]  = useState([]);
  useEffect(() => {
    API.get('/hr/departments').then(r =>
      setDeptOpts((r.data || []).map(d => d.name).filter(Boolean))
    ).catch(() => setDeptOpts([]));
    API.get('/hr/positions').then(r =>
      setPosOpts((r.data || []).map(p => p.name).filter(Boolean))
    ).catch(() => setPosOpts([]));
  }, []);

  // ── Wizard state ──────────────────────────────────────────────
  // 5 steps: 0 Personal · 1 Employment · 2 Salary & Payroll · 3 Banking · 4 Next of kin
  const [step, setStep] = useState(0);
  const STEPS = ['Personal', 'Employment', 'Salary & Payroll', 'Allowances', 'Banking', 'Next of kin'];

  // Allowance state for the wizard's last step. `allowanceRows` holds
  // one entry per active allowance type; the user ticks `is_assigned`
  // to opt in, then either edits the amount (mode=amount) or the
  // percentage (mode=percentage). The cedi figure for percentage rows
  // is shown live as "X% of basic = GHS Y" using the basic_salary the
  // operator entered in Step 3.
  const [allowanceRows, setAllowanceRows] = useState([]);
  const [allowancesLoading, setAllowancesLoading] = useState(false);
  const loadAllowanceCatalog = async (trans_code) => {
    setAllowancesLoading(true);
    try {
      // When editing, fetch the per-employee saved rows. When adding,
      // fetch the catalog with everything un-assigned (employee_id=0).
      const url = trans_code
        ? `/employees/${trans_code}/allowances`
        : `/employees/0/allowances`;
      const r = await API.get(url);
      setAllowanceRows((r.data || []).map(x => ({
        ...x,
        mode: x.mode || (x.type_calc_mode === 'percentage' ? 'percentage' : 'amount'),
        amount: Number(x.amount) || 0,
        percentage: Number(x.percentage) || Number(x.default_percentage) || 0,
      })));
    } catch { setAllowanceRows([]); }
    finally { setAllowancesLoading(false); }
  };
  const setAllowRow = (i, patch) =>
    setAllowanceRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
  const allowanceLineAmount = (r, basic) => {
    if (!r.is_assigned) return 0;
    if (r.mode === 'percentage') return Math.round((Number(basic || 0) * Number(r.percentage || 0) / 100) * 100) / 100;
    return Math.max(0, Number(r.amount) || 0);
  };
  const allowanceTotalFor = (basic) =>
    allowanceRows.reduce((s, r) => s + allowanceLineAmount(r, basic), 0);

  // Code mode: 'manual' lets the user type, 'auto' fetches a unique code
  // from /api/employees/next-code. Edit mode is always manual (we don't
  // re-generate an existing record's code).
  const [codeMode, setCodeMode] = useState('manual');
  const [codePrefix, setCodePrefix] = useState('EMP');
  const [genLoading, setGenLoading] = useState(false);
  const [genErr, setGenErr] = useState('');

  // UI-only flag for "Pays SSNIT?". Stored as derived state because the
  // DB only has ssnit_number — turning the flag OFF clears the number.
  // Defaults true on new (most staff do pay), derives from existing
  // value on edit.
  const [paysSsnit, setPaysSsnit] = useState(true);

  const generateCode = async (prefix = codePrefix) => {
    setGenLoading(true); setGenErr('');
    try {
      const r = await API.get('/employees/next-code', { params: { prefix } });
      setForm(f => ({ ...f, employee_code: r.data?.code || '' }));
    } catch (e) {
      setGenErr(e.response?.data?.message || 'Could not generate code');
    } finally { setGenLoading(false); }
  };

  const load = async () => {
    setL(true);
    try {
      const params = {};
      if (q)   params.q = q;
      if (dep) params.department = dep;
      if (emp) params.employment_type = emp;
      setRows((await API.get('/employees', { params })).data);
    } catch (e) { setErr(e.response?.data?.message || 'Load failed'); }
    finally { setL(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, dep, emp]);

  // ── KPIs ──
  const summary = useMemo(() => {
    const total = rows.length;
    const onLeave = rows.filter(r => r.leav === 'yes').length;
    const gross = rows.reduce((s, r) => s + Number(r.basic_salary || 0), 0);
    const deptCount = new Set(rows.map(r => r.department).filter(Boolean)).size;
    return { total, onLeave, gross, deptCount };
  }, [rows]);

  const openNew  = () => {
    setEd(null); setForm(blank); setErr(''); setStep(0);
    setCodeMode('manual'); setGenErr('');
    setPaysSsnit(true);
    loadAllowanceCatalog(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEd(r);
    setForm({ ...blank, ...r,
      date_of_birth: r.date_of_birth ? String(r.date_of_birth).slice(0, 10) : '',
      join_date:     r.join_date     ? String(r.join_date).slice(0, 10)     : '',
    });
    setErr(''); setStep(0);
    setCodeMode('manual'); setGenErr('');
    setPaysSsnit(!!r.ssnit_number);
    loadAllowanceCatalog(r.trans_code);
    setOpen(true);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Per-step required-field gating. Returns a list of missing field
  // labels, or [] when the step is complete. Drives whether the Next
  // button is enabled and what the inline alert says.
  //
  // Step indices mirror STEPS above:
  //   0 Personal · 1 Employment · 2 Salary & Payroll ·
  //   3 Allowances · 4 Banking + Statutory · 5 Next of kin
  const stepErrors = (i) => {
    const missing = [];
    if (i === 0) {
      if (!form.employee_code?.trim()) missing.push('Employee code');
      if (!form.first_name?.trim())    missing.push('First name');
      if (!form.last_name?.trim())     missing.push('Last name');
      if (!form.date_of_birth)         missing.push('Date of birth');
      if (!form.phone?.trim())         missing.push('Phone');
      else if (!/^\d{10}$/.test(form.phone.trim().replace(/\s+/g, '')))
                                       missing.push('Phone (must be exactly 10 digits)');
      if (!form.address?.trim())       missing.push('Address');
    }
    if (i === 1) {
      if (!form.designation?.trim())   missing.push('Designation');
      if (!form.department?.trim())    missing.push('Department');
      if (!form.employment_type)       missing.push('Employment type');
      if (!form.join_date)             missing.push('Join / hire date');
    }
    if (i === 2) {
      if (form.basic_salary == null || form.basic_salary === '' || Number(form.basic_salary) <= 0)
                                       missing.push('Basic salary (greater than 0)');
      if (!form.payroll_mode)          missing.push('Payment mode');
    }
    // Step 3 (Allowances) has no required ticks — every row is optional.
    if (i === 4) {
      if (form.payroll_mode === 'Bank') {
        if (!form.bank_name?.trim())     missing.push('Bank name');
        if (!form.account_number?.trim()) missing.push('Account number');
      }
      if (paysSsnit && !form.ssnit_number?.trim()) missing.push('SSNIT number');
    }
    return missing;
  };
  const canAdvance = stepErrors(step).length === 0;
  const goNext = () => {
    const miss = stepErrors(step);
    if (miss.length) { setErr(`Please fill: ${miss.join(', ')}`); return; }
    setErr('');
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };
  const goBack = () => { setErr(''); setStep(s => Math.max(0, s - 1)); };

  const submit = async () => {
    // Final guard — covers every required field across all steps in
    // case the user jumped the stepper somehow (or paste-edits past
    // the gating). Mirrors the per-step rules in stepErrors().
    const allMissing = [
      ...stepErrors(0), ...stepErrors(1),
      ...stepErrors(2), ...stepErrors(3),
      ...stepErrors(4),
    ];
    if (allMissing.length) {
      setErr(`Please complete: ${allMissing.join(', ')}`);
      return;
    }
    // Clear SSNIT number when the toggle is off, regardless of what's
    // in the field. Belt-and-braces — the field is hidden when off.
    if (!paysSsnit) form.ssnit_number = '';
    setSav(true); setErr('');
    try {
      // Build the allowance payload once — used in both create + edit.
      const allowancePayload = allowanceRows
        .filter(r => r.is_assigned)
        .map(r => ({
          allowance_type_id: r.allowance_type_id,
          mode: r.mode === 'percentage' ? 'percentage' : 'amount',
          percentage: Number(r.percentage) || 0,
          amount: Number(r.amount) || 0,
        }));

      if (editing) {
        await API.put(`/employees/${editing.trans_code}`, form);
        await API.put(`/employees/${editing.trans_code}/allowances`,
          { items: allowancePayload }).catch(() => {});
        setOpen(false);
        swal.success(`${form.first_name} ${form.last_name} updated`);
      } else {
        const r = await API.post('/employees', form);
        const newId = r.data?.trans_code;
        if (newId && allowancePayload.length > 0) {
          await API.put(`/employees/${newId}/allowances`,
            { items: allowancePayload }).catch(() => {});
        }
        const name = `${form.first_name} ${form.last_name}`;
        // Carry common fields for serial entry, reset the rest, and
        // bounce the wizard back to step 1 so the operator can start
        // the next employee without clicking Back four times.
        setForm((f) => ({
          ...blank, department: f.department, employment_type: f.employment_type,
          payroll_mode: f.payroll_mode,
        }));
        setStep(0);
        setPaysSsnit(true);
        // Reset the allowance picks for the next employee.
        loadAllowanceCatalog(null);
        // If they had auto-code on, fetch a fresh unique code right
        // away so the new form opens already filled at Step 1.
        if (codeMode === 'auto') generateCode(codePrefix);
        swal.success(`${name} added to staff register — add another or close`);
      }
      await load();
    } catch (e) { setErr(swal.pickError(e, 'Save failed')); }
    finally { setSav(false); }
  };

  const remove = async (r) => {
    const ok = await swal.confirmDelete(`employee ${r.first_name} ${r.last_name}`);
    if (!ok) return;
    try {
      await API.delete(`/employees/${r.trans_code}`);
      swal.success('Employee removed');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const departments = useMemo(() =>
    Array.from(new Set(rows.map(r => r.department).filter(Boolean))).sort(),
  [rows]);
  const clearFilters = () => { setQ(''); setDep(''); setEmp(''); };
  const hasFilters = q || dep || emp;

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <ModuleHeader icon={<BadgeIcon sx={{ fontSize: 28 }} />}
        title="Employees"
        subtitle="Staff register — legacy panel. Use HR Employees for payroll-grade records." />

      {/* ── Summary strip ──────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
        <SummaryTile tone="primary" icon={<PeopleIcon />} label="Showing"      value={summary.total} />
        <SummaryTile tone="info"    icon={<DeptIcon />}   label="Departments"  value={summary.deptCount} />
        <SummaryTile tone="warning" icon={<LeaveIcon />}  label="On leave"     value={summary.onLeave} />
        <SummaryTile tone="success" icon={<SalaryIcon />} label="Total basic salary"
          value={`GHS ${fmtMoney(summary.gross)}`} wide />
      </Stack>

      {/* ── Toolbar card ───────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}
            alignItems={{ md: 'center' }}>
            <TextField size="small" placeholder="Search name, code, phone…"
              value={q} onChange={(e) => setQ(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQ('')}><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }} />
            <TextField select size="small" label="Department" value={dep}
              onChange={(e) => setDep(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Employment" value={emp}
              onChange={(e) => setEmp(e.target.value)} sx={{ minWidth: 170 }}>
              <MenuItem value=""><em>All</em></MenuItem>
              {EMP_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {hasFilters && (
              <Button startIcon={<FilterIcon />} onClick={clearFilters}
                size="small" sx={{ textTransform: 'none' }}>Clear</Button>
            )}
            <Box sx={{ flex: { md: 1 } }} />
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Reload">
                <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
                sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}>
                New employee
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

      {/* ── Table ──────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <TableContainer>
          <Table size="small" sx={PRO_TABLE}>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="right">Basic salary</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={22} />
                </TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"
                  sx={{ color: 'text.secondary', fontStyle: 'italic', py: 6 }}>
                  <PersonIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                  <Typography>No employees match the current filters.</Typography>
                  {hasFilters && (
                    <Button size="small" onClick={clearFilters}
                      sx={{ mt: 1, textTransform: 'none' }}>Clear filters</Button>
                  )}
                </TableCell></TableRow>
              )}
              {!loading && rows.map((r) => {
                const tone = EMP_TYPE_TONE[r.employment_type] || 'default';
                const onLeave = r.leav === 'yes';
                return (
                  <TableRow key={r.trans_code} hover className={onLeave ? 'row-leave' : ''}>
                    <TableCell>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{
                          width: 34, height: 34, fontSize: 12.5, fontWeight: 800,
                          bgcolor: onLeave ? 'grey.400' : 'primary.main',
                        }}>
                          {initials(r.first_name, r.last_name) || '?'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }} noWrap>
                            {r.first_name} {r.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                            {r.employee_code}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{r.designation || '—'}</TableCell>
                    <TableCell>{r.department || '—'}</TableCell>
                    <TableCell>{r.phone || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(r.basic_salary)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.employment_type || '—'}
                        color={tone} variant={tone === 'default' ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
                    </TableCell>
                    <TableCell align="center">
                      {onLeave
                        ? <Chip size="small" color="warning" label="On leave"
                            sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
                        : <Chip size="small" color="success" label="Active"
                            sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => remove(r)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Add / Edit dialog ──────────────────────────────────── */}
      {/* onClose ignores backdrop clicks + Escape so the user can't lose
          a half-filled wizard by tapping outside the dialog. The X
          button and Cancel still close it normally. */}
      <Dialog open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 2, minHeight: '85vh' } }}>
        {/* Gradient header */}
        <Box sx={{
          px: 3, py: 2,
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 40, height: 40, fontWeight: 800 }}>
            {editing ? initials(editing.first_name, editing.last_name) || <BadgeIcon /> : <AddIcon />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }} noWrap>
              {editing ? `${editing.first_name} ${editing.last_name}` : 'New employee'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {editing
                ? `Code: ${editing.employee_code || '—'} · ${editing.leav === 'yes' ? 'On leave' : 'Active'}`
                : 'Personal · Employment · Salary & Payroll · Banking · Next of kin'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stepper rail just below the gradient header */}
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
          <Stepper activeStep={step} alternativeLabel
            sx={{
              '& .MuiStepLabel-label': { fontSize: 11.5, fontWeight: 600, mt: 0.5 },
              '& .MuiStepLabel-label.Mui-active': { fontWeight: 800, color: 'primary.main' },
              '& .MuiStepLabel-label.Mui-completed': { fontWeight: 700, color: 'success.main' },
              '& .MuiStepIcon-root': { fontSize: 24 },
            }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent dividers sx={{ bgcolor: '#fafbfc', p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

          {/* Step 0 — Personal */}
          {step === 0 && (
            <Section title="Personal information" icon={<PersonIcon sx={{ fontSize: 16 }} />}>
              {/* ── Employee-code mode picker ── */}
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                  bgcolor: '#fff' }}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: 11, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary', mb: 0.5 }}>
                      Employee code
                    </FormLabel>
                    <RadioGroup row value={codeMode}
                      onChange={(e) => {
                        const m = e.target.value;
                        setCodeMode(m);
                        if (m === 'auto' && !editing) generateCode(codePrefix);
                        if (m === 'manual') setForm(f => ({ ...f, employee_code: editing?.employee_code || '' }));
                      }}>
                      <FormControlLabel value="manual" control={<Radio size="small" />}
                        label={<Typography variant="body2">Manual — I'll type the code myself</Typography>} />
                      <FormControlLabel value="auto" control={<Radio size="small" />} disabled={!!editing}
                        label={<Typography variant="body2">Auto-generate (unique, checked in DB)</Typography>} />
                    </RadioGroup>
                  </FormControl>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.2 }}>
                    {codeMode === 'auto' && (
                      <TextField size="small" label="Prefix" value={codePrefix}
                        onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
                        sx={{ width: 130 }}
                        helperText="Letters only (e.g. EMP, STF)" />
                    )}
                    <TextField size="small" label="Code *" value={form.employee_code}
                      onChange={(e) => setF('employee_code', e.target.value)}
                      disabled={codeMode === 'auto'}
                      sx={{ flex: 1, minWidth: 200 }}
                      InputProps={{
                        sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
                        endAdornment: codeMode === 'auto' ? (
                          <InputAdornment position="end">
                            <Tooltip title="Generate another">
                              <span>
                                <IconButton size="small" onClick={() => generateCode(codePrefix)} disabled={genLoading}>
                                  {genLoading ? <CircularProgress size={14} /> : <AutoIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ) : null,
                      }} />
                  </Stack>
                  {genErr && <Alert severity="warning" sx={{ mt: 1 }}>{genErr}</Alert>}
                </Box>
              </Grid>

              <Field name="first_name" label="First name *" value={form.first_name} onChange={setF} sm={6} required />
              <Field name="last_name"  label="Last name *"  value={form.last_name}  onChange={setF} sm={6} required />
              <Field name="gender" label="Gender" value={form.gender} onChange={setF}
                select options={['Male', 'Female', 'Other']} sm={4} />
              <Field name="date_of_birth" label="Date of birth *" type="date" value={form.date_of_birth} onChange={setF} sm={4} required />
              <Field name="phone" label="Phone *" value={form.phone} onChange={setF} sm={4} required />
              <Field name="email" label="Email" type="email" value={form.email} onChange={setF} sm={12} />
              <Field name="address" label="Address *" value={form.address} onChange={setF} sm={12}
                multiline rows={2} required />
            </Section>
          )}

          {/* Step 1 — Employment */}
          {step === 1 && (
            <Section title="Employment" icon={<WorkIcon sx={{ fontSize: 16 }} />}>
              {/* Designation + Department — freeSolo so the user can pick
                  from the preset list OR type a custom value. */}
              <Grid item xs={12} sm={6}>
                <Autocomplete freeSolo size="small" options={posOpts}
                  value={form.designation || ''}
                  onChange={(_, v) => setF('designation', v ?? '')}
                  onInputChange={(_, v) => setF('designation', v ?? '')}
                  renderInput={(p) => <TextField {...p} fullWidth size="small" required
                    label="Designation *"
                    helperText="Pick from HR Settings → Positions, or type a custom one" />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete freeSolo size="small" options={deptOpts}
                  value={form.department || ''}
                  onChange={(_, v) => setF('department', v ?? '')}
                  onInputChange={(_, v) => setF('department', v ?? '')}
                  renderInput={(p) => <TextField {...p} fullWidth size="small" required
                    label="Department *"
                    helperText="Pick from HR Settings → Departments, or type a custom one" />} />
              </Grid>

              <Field name="employment_type" label="Employment type *" value={form.employment_type} onChange={setF}
                select options={EMP_TYPES} sm={6} required />
              <Field name="join_date" label="Join / hire date *" type="date" value={form.join_date} onChange={setF} sm={6} required />
              <Field name="leav" label="On leave?" value={form.leav} onChange={setF}
                select options={[{ value: 'no', label: 'No — currently active' }, { value: 'yes', label: 'Yes — on leave' }]} sm={12} />
            </Section>
          )}

          {/* Step 2 — Salary & Payroll */}
          {step === 2 && (() => {
            // Salary label + helper text vary by employment type. The
            // single basic_salary column always stores the canonical
            // monthly figure used by payroll; for Casual it represents
            // the daily rate × typical days (the user enters whichever
            // makes sense for them, helper text spells out the meaning).
            const t = form.employment_type;
            const salaryLabel =
              t === 'Casual'   ? 'Daily rate (GHS) *' :
              t === 'Contract' ? 'Monthly contract fee (GHS) *' :
                                 'Monthly basic salary (GHS) *';
            const salaryHelp =
              t === 'Casual'   ? 'Amount paid per day worked. Payroll multiplies by days × month.' :
              t === 'Contract' ? 'Flat amount paid every month for the duration of the contract.' :
                                 'Monthly salary paid to permanent staff. Feeds SSNIT + PAYE.';
            const tone =
              t === 'Casual'   ? 'warning' :
              t === 'Contract' ? 'info'    : 'success';
            return (
              <Section title="Salary & Payroll" icon={<SalaryIcon sx={{ fontSize: 16 }} />}>
                <Field name="basic_salary" label={salaryLabel} type="number"
                  value={form.basic_salary} onChange={setF} sm={6} required
                  inputProps={{ min: 0, step: '0.01' }}
                  helperText={salaryHelp} />
                <Field name="payroll_mode" label="Payment mode *" value={form.payroll_mode} onChange={setF}
                  select options={[
                    { value: 'TableTop', label: 'TableTop (cash)' },
                    { value: 'Bank',     label: 'Bank transfer' },
                  ]} sm={6} required
                  helperText="Bank requires banking details on the next step" />
                <Grid item xs={12}>
                  <Box sx={{ p: 1.4, borderRadius: 1,
                    bgcolor: (th) => alpha(th.palette[tone].main, 0.08),
                    border: '1px dashed', borderColor: `${tone}.light`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700,
                        color: `${tone}.dark`, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        {t === 'Casual' ? 'Daily rate' : t === 'Contract' ? 'Monthly contract' : 'Monthly basic'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        Stored for payroll on the {t || 'employment'} schedule
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: `${tone}.dark`, fontSize: 18 }}>
                      GHS {fmtMoney(form.basic_salary)}
                    </Typography>
                  </Box>
                </Grid>
              </Section>
            );
          })()}

          {/* Step 4 — Banking & Statutory */}
          {step === 4 && (
            <>
              {/* Banking — only required (and prominent) when Bank mode */}
              <Section title="Banking" icon={<BankIcon sx={{ fontSize: 16 }} />}>
                {form.payroll_mode === 'Bank' ? (
                  <>
                    <Field name="bank_name"      label="Bank name *"      value={form.bank_name}      onChange={setF} sm={6} required />
                    <Field name="account_number" label="Account number *" value={form.account_number} onChange={setF} sm={6} required />
                    <Field name="bank_branch"    label="Branch (optional)" value={form.bank_branch || ''}
                      onChange={setF} sm={12} />
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info" variant="outlined">
                      Payment mode is <b>TableTop (cash)</b>. Banking details are not
                      required. Switch to <b>Bank</b> on the previous step if this
                      employee is paid by transfer.
                    </Alert>
                  </Grid>
                )}
              </Section>

              {/* Statutory — SSNIT toggle controls whether we deduct */}
              <Section title="Statutory" icon={<SalaryIcon sx={{ fontSize: 16 }} />}>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                    bgcolor: '#fff' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={paysSsnit}
                          onClick={() => {
                            const next = !paysSsnit;
                            setPaysSsnit(next);
                            if (!next) setF('ssnit_number', '');
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                            This employee pays SSNIT
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Toggle off for staff not on the social-security scheme.
                            When off, SSNIT is skipped entirely on their payslip.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>

                {paysSsnit && (
                  <Field name="ssnit_number" label="SSNIT number *" value={form.ssnit_number}
                    onChange={setF} sm={6} required />
                )}
                <Field name="pin_number" label="PIN (optional)" value={form.pin_number}
                  onChange={setF} sm={paysSsnit ? 6 : 12} />
              </Section>
            </>
          )}

          {/* Step 5 — Next of kin (final) */}
          {step === 5 && (
            <Section title="Next of kin" icon={<KinIcon sx={{ fontSize: 16 }} />}>
              <Field name="next_of_kin"       label="Name"  value={form.next_of_kin}       onChange={setF} sm={8} />
              <Field name="next_of_kin_phone" label="Phone" value={form.next_of_kin_phone} onChange={setF} sm={4} />
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined" icon={<DoneIcon />}>
                  Last step. Review the previous tabs with <b>Back</b> if needed, then click
                  <b> {editing ? 'Save changes' : 'Create employee'}</b>.
                </Alert>
              </Grid>
            </Section>
          )}

          {/* Step 3 — Allowances. Sits right after Salary & Payroll so
              the operator can see the basic salary they just entered
              while picking allowances. Percentage rows compute live
              against form.basic_salary. */}
          {step === 3 && (
            <Section title="Allowances" icon={<AllowanceIcon sx={{ fontSize: 16 }} />}>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                  Tick what this employee enjoys. Percentage allowances are
                  computed from their basic salary
                  <b> (GHS {fmtMoney(form.basic_salary)})</b>. Save creates
                  the employee with these allowances in one go.
                </Alert>
              </Grid>

              {allowancesLoading && (
                <Grid item xs={12} sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Grid>
              )}

              {!allowancesLoading && allowanceRows.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" variant="outlined">
                    No allowance types defined yet. Add them under
                    <b> HR → HR Settings → Allowance types</b>. You can
                    skip this step and add allowances later.
                  </Alert>
                </Grid>
              )}

              {!allowancesLoading && allowanceRows.length > 0 && (
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 800, fontSize: 11, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
                          <TableCell sx={{ width: 60 }}>Pick</TableCell>
                          <TableCell>Allowance</TableCell>
                          <TableCell>Mode</TableCell>
                          <TableCell align="right" sx={{ width: 150 }}>Amount / Percentage</TableCell>
                          <TableCell align="right" sx={{ width: 140 }}>Cedi value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allowanceRows.map((r, i) => {
                          const cedi = allowanceLineAmount(r, form.basic_salary);
                          return (
                            <TableRow key={r.allowance_type_id} hover>
                              <TableCell align="center">
                                <Checkbox size="small" checked={!!r.is_assigned}
                                  onChange={(e) => setAllowRow(i, {
                                    is_assigned: e.target.checked,
                                    // pre-fill from type defaults when first ticked
                                    amount: e.target.checked && !Number(r.amount)
                                      ? Number(r.default_amount) || 0 : r.amount,
                                    percentage: e.target.checked && !Number(r.percentage)
                                      ? Number(r.default_percentage) || 0 : r.percentage,
                                  })} />
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{r.name}</Typography>
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.3 }}>
                                  {r.is_taxable && <Chip size="small" color="warning" label="Taxable" sx={{ fontWeight: 700, fontSize: 10, height: 18 }} />}
                                  {r.is_pensionable && <Chip size="small" color="info" label="Pensionable" sx={{ fontWeight: 700, fontSize: 10, height: 18 }} />}
                                </Stack>
                                {r.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                                    {r.description}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <TextField select size="small" value={r.mode}
                                  disabled={!r.is_assigned}
                                  onChange={(e) => setAllowRow(i, { mode: e.target.value })}
                                  sx={{ minWidth: 130 }}>
                                  <MenuItem value="amount">Fixed amount</MenuItem>
                                  <MenuItem value="percentage">% of basic</MenuItem>
                                </TextField>
                              </TableCell>
                              <TableCell align="right">
                                {r.mode === 'percentage' ? (
                                  <TextField size="small" type="number" value={r.percentage ?? 0}
                                    disabled={!r.is_assigned}
                                    onChange={(e) => setAllowRow(i, { percentage: e.target.value })}
                                    sx={{ width: 130 }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} />
                                ) : (
                                  <TextField size="small" type="number" value={r.amount ?? 0}
                                    disabled={!r.is_assigned}
                                    onChange={(e) => setAllowRow(i, { amount: e.target.value })}
                                    sx={{ width: 130 }}
                                    inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700,
                                color: r.is_assigned ? 'success.dark' : 'text.disabled' }}>
                                GHS {fmtMoney(cedi)}
                                {r.mode === 'percentage' && r.is_assigned && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {Number(r.percentage || 0).toFixed(2)}% of {fmtMoney(form.basic_salary)}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
                          <TableCell colSpan={4} align="right">TOTAL ALLOWANCES</TableCell>
                          <TableCell align="right" sx={{ fontSize: 16, color: 'success.dark' }}>
                            GHS {fmtMoney(allowanceTotalFor(form.basic_salary))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

            </Section>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.8, bgcolor: 'background.paper',
          borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {/* Step indicator + nav */}
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1.5, fontWeight: 700 }}>
            Step {step + 1} of {STEPS.length}
          </Typography>
          {step > 0 && (
            <Button startIcon={<PrevIcon />} onClick={goBack}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Back
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<NextIcon />} onClick={goNext} disabled={!canAdvance}
              sx={{ textTransform: 'none', fontWeight: 800, px: 2.5 }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={submit} disabled={saving}
              size="large" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <DoneIcon />}
              sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
              {editing ? 'Save changes' : 'Create employee'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// SummaryTile — strip card
// ──────────────────────────────────────────────────────────────────
function SummaryTile({ icon, label, value, tone = 'primary', wide }) {
  return (
    <Card sx={{
      flex: wide ? 1.4 : 1, minWidth: 0, borderRadius: 2,
      borderLeft: 4, borderColor: `${tone}.main`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`,
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5,
                  textTransform: 'uppercase', fontSize: 10, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.25 }} noWrap>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
